import { Injectable } from '@angular/core';
import { CognitoUserService } from './cognito-user.service';

export enum DeviceType {
  NONE, PHONE, DESKTOP
}

export interface NavEntity {
  visible: boolean;
  showWhenOffline: boolean;
  title: string;
  icon: string;
  prefix: string;
  page: string;
  hierarchy: string[];
  message: string;
  admin: boolean;
  superAdmin: boolean;
  container: boolean;
  deviceType: DeviceType;
  open: boolean;
}

// Positional factory for NavEntity - the array below used to be 73 near-identical object
// literals (same 13 keys, repeated every time), which is exactly the kind of duplication
// SonarQube's copy-paste detector flags even though the actual values differ per entry.
// This is a mechanical, generated transformation (see dedupe-nav.js in git history) -
// every argument was extracted from the original object literals' exact source text via
// the TypeScript compiler API, not retyped by hand, so the resulting data is identical.
function nav(visible: boolean, showWhenOffline: boolean, title: string, icon: string, prefix: string,
             page: string, hierarchy: string[], message: string, admin: boolean, superAdmin: boolean,
             container: boolean, deviceType: DeviceType, open: boolean): NavEntity {
  return { visible, showWhenOffline, title, icon, prefix, page, hierarchy, message, admin, superAdmin, container, deviceType, open };
}

@Injectable({
  providedIn: 'root'
})
export class CatchitNavigationService {
  private navEntities: NavEntity[];

  constructor(private catchitUserService: CognitoUserService) {
    this.navEntities = [
      // Top-level pages
      nav(true, true, 'Home', 'faHome', 'fas', '/home', [], '', false, false, false, DeviceType.NONE, false),
      // Home Page hierarchy - Core user functions
      nav(true, false, 'Enter Line Check', 'faEdit', 'fas', '/enter-data', ['/home'], 'Use the smart table to fill in the results of a line check.', false, false, false, DeviceType.DESKTOP, false),
      nav(true, true, 'Enter Line Check (Offline)', 'faEdit', 'fas', '/offline-enter-data', ['/home'], 'Use the phone-friendly data entry to enter a line check.', false, false, false, DeviceType.PHONE, false),
      nav(false, true, 'Enter Line Check', 'faEdit', 'fas', '/offline-enter-data/swipe-data-entry', ['/home'], 'Phone-friendly swipe interface for entering line check data.', false, false, false, DeviceType.PHONE, false),
      nav(true, true, 'Manage Data', 'faClipboardList', 'fas', '/manage-data', ['/home'], 'View and download the data you have collected from each of your lines.', false, false, true, DeviceType.NONE, false),
      nav(true, false, 'Traps & Devices', 'faCube', 'fas', '/traps-devices', ['/home'], 'Make changes to your traps and devices.', false, false, true, DeviceType.NONE, false),
      // Traps & Devices sub-pages
      nav(true, true, 'Field Notes', 'faClipboard', 'fas', '/field-notes', ['/home', '/traps-devices'], 'View and print Field Notes for the devices on a line.', false, false, false, DeviceType.NONE, false),
      nav(true, false, 'Add Device', 'faPlusSquare', 'fas', '/add-devices', ['/home', '/traps-devices'], 'Add a device to a line.', true, false, false, DeviceType.NONE, false),
      nav(true, false, 'Remove Device', 'faMinusSquare', 'fas', '/remove-devices', ['/home', '/traps-devices'], 'Remove devices from a line.', true, false, false, DeviceType.NONE, false),
      nav(true, false, 'View & Edit Device Properties', 'faListAlt', 'fas', '/edit-device-properties', ['/home', '/traps-devices'], 'Change the name, type and/or comments for individual devices on a line.', false, false, false, DeviceType.NONE, false),
      nav(true, false, 'Move Device', 'faArrowsAlt', 'fas', '/move-devices', ['/home', '/traps-devices'], 'Use the map or enter new coordinates to move one or more devices on a line.', false, false, false, DeviceType.NONE, false),
      nav(true, false, 'Edit Device Order', 'faSort', 'fas', '/device-order', ['/home', '/traps-devices'], 'Change the order of the devices on a line.', false, false, false, DeviceType.NONE, false),
      nav(true, false, 'Edit Default Trap Lures', 'faDrumstickBite', 'fas', '/default-baits', ['/home', '/traps-devices'], 'Change the default lures associated with each trap on a line.', false, false, false, DeviceType.NONE, false),
      nav(true, false, 'Bulk Change Device Type', 'faCubes', 'fas', '/bulk-change-device-type', ['/home', '/traps-devices'], 'Change the current type of multiple devices on a line.', true, false, false, DeviceType.NONE, false),
      nav(true, false, 'Reinstate Device', 'faHistory', 'fas', '/reinstate-devices', ['/home', '/traps-devices'], 'Reinstate previously removed devices on a line or modify the removal date of devices.', true, false, false, DeviceType.NONE, false),
      nav(true, false, 'Rivers & Tracks', 'faDirections', 'fas', '/rivers-tracks', ['/home'], 'Configure the locations of rivers and tracks.', true, false, false, DeviceType.NONE, false),
      nav(true, false, 'Graphs', 'faChartBar', 'fas', '/graphs', ['/home'], 'View graphs of your trap data.', false, false, false, DeviceType.NONE, false),
      nav(true, false, 'Account Settings', 'faUserCog', 'fas', '/account-settings', ['/home'], 'Configure your account.', false, false, true, DeviceType.NONE, false),
      nav(true, false, 'Tutorials', 'faFilm', 'fas', '/tutorials', ['/home'], 'View video tutorials to learn how to get the most out of CatchIT.', false, false, false, DeviceType.NONE, false),
      // Admin-only sections
      nav(true, true, 'Administration', 'faCogs', 'fas', '/admin', ['/home'], 'Configure areas, lines, and permissions.', true, false, true, DeviceType.NONE, false),
      nav(true, false, 'Enter Line Check', 'faEdit', 'fas', '/enter-data', ['/home'], 'Use the smart table to fill in the results of a line check.', false, false, false, DeviceType.PHONE, false),
      nav(true, false, 'Enter Line Check (Offline)', 'faEdit', 'fas', '/offline-enter-data', ['/home'], 'Use the phone-friendly data entry to enter a line check.', false, false, false, DeviceType.DESKTOP, false),
      // Account Settings sub-pages
      nav(true, false, 'Profile', 'faUser', 'fas', '/profile', ['/home', '/account-settings'], 'View and edit your personal profile information and preferences.', false, false, false, DeviceType.NONE, false),
      nav(true, false, 'Line Check Email Settings', 'faEnvelope', 'fas', '/email-settings', ['/home', '/account-settings'], 'Configure which lines will display emails and which will send emails when a line check has been made.', false, false, false, DeviceType.NONE, false),
      nav(true, false, 'Reset Password', 'faKey', 'fas', '/reset-password', ['/home', '/account-settings'], 'Reset your existing password.', false, false, false, DeviceType.NONE, false),
      // Manage Data sub-pages
      nav(true, false, 'View Data', 'faTable', 'fas', '/view-data', ['/home', '/manage-data'], 'View the data you have collected from each of your lines.', false, false, false, DeviceType.NONE, false),
      nav(true, true, 'Stored Submissions', 'faTable', 'fas', '/stored-submissions', ['/home', '/manage-data'], 'View and upload any submissions stored on your mobile device or computer.', false, false, false, DeviceType.NONE, false),
      nav(true, false, 'Download CSV Files', 'faFileDownload', 'fas', '/export-data', ['/home', '/manage-data'], 'Download CSV files containing line check and device information by area.', false, false, false, DeviceType.NONE, false),
      nav(true, false, 'Refresh CSV File Data', 'faRedoAlt', 'fas', '/refresh-data', ['/home', '/manage-data'], 'Regenerate the CSV files behind Download CSV Files, per area, so they reflect the latest data.', true, false, false, DeviceType.NONE, false),
      // Super Admin sections
      // Admin landing pages
      nav(true, false, 'Areas', 'faMapLocationDot', 'fas', '/areas', ['/home', '/admin'], 'Manage areas, create new areas, set defaults for species and lures.', true, false, true, DeviceType.NONE, false),
      nav(true, false, 'Default Lures', 'faWrench', 'fas', '/default-lures', ['/home', '/admin', '/areas'], 'Set preferred lures for areas.', true, false, false, DeviceType.NONE, false),
      nav(true, false, 'Default Species', 'faPaw', 'fas', '/default-species', ['/home', '/admin', '/areas'], 'Set preferred species for areas.', true, false, false, DeviceType.NONE, false),
      nav(true, false, 'Rename Areas', 'faPencilAlt', 'fas', '/rename-areas', ['/home', '/admin', '/areas'], 'Change the name of existing areas.', true, false, false, DeviceType.NONE, false),
      nav(true, false, 'Create Area', 'faPlusCircle', 'fas', '/create-area', ['/home', '/admin', '/areas'], 'Create a new area with custom map boundaries.', true, false, false, DeviceType.NONE, false),
      nav(true, false, 'Generate GPX Files', 'faFileDownload', 'fas', '/generate-gpx', ['/home', '/admin', '/areas'], 'Generate GPS track files for your area lines.', true, false, false, DeviceType.NONE, false),
      nav(true, false, 'View All Lines in Area', 'faRoute', 'fas', '/view-all-lines-in-area', ['/home', '/admin', '/areas'], 'View and interact with all lines in the selected area on a map.', true, false, false, DeviceType.NONE, false),
      nav(true, false, 'Lines', 'faRoute', 'fas', '/lines', ['/home', '/admin'], 'Manage lines, add/rename lines, control access, and link devices.', true, false, true, DeviceType.NONE, false),
      nav(true, false, 'People', 'faUsers', 'fas', '/people', ['/home', '/admin'], 'Manage users, permissions, announcements, and email settings.', true, false, true, DeviceType.NONE, false),
      nav(true, false, 'Announcements', 'faEnvelope', 'fas', '/announcements', ['/home', '/admin', '/people'], 'Send announcements to users via email with attachments.', true, false, false, DeviceType.NONE, false),
      nav(true, false, 'Add Users', 'faUserPlus', 'fas', '/add-users', ['/home', '/admin', '/people'], 'Add new users to CatchIT with role and line assignments.', true, false, false, DeviceType.NONE, false),
      nav(true, false, 'Area Permissions', 'faUserShield', 'fas', '/area-permissions', ['/home', '/admin', '/people'], 'Manage admin permissions for areas.', true, false, false, DeviceType.NONE, false),
      nav(true, false, 'Line Permissions', 'faLock', 'fas', '/line-permissions', ['/home', '/admin', '/people'], 'Configure which lines a user has access.', true, false, false, DeviceType.NONE, false),
      nav(true, false, 'Manage Emails', 'faEnvelope', 'fas', '/manage-emails', ['/home', '/admin', '/people'], 'Manage email recipients for line notifications.', true, false, false, DeviceType.NONE, false),
      nav(true, false, 'Configure Super Admins', 'faUserShield', 'fas', '/super-user-admin', ['/home', '/admin', '/people'], 'Configure which users are super admins.', false, true, false, DeviceType.NONE, false),
      nav(true, false, 'Add Lines', 'faPlusCircle', 'fas', '/add-lines', ['/home', '/admin', '/lines'], 'Create new trap lines, bait station lines, or bird station lines.', true, false, false, DeviceType.NONE, false),
      nav(true, false, 'Rename Lines', 'faEdit', 'fas', '/rename-lines', ['/home', '/admin', '/lines'], 'Rename existing lines (all historical data will be updated).', true, false, false, DeviceType.NONE, false),
      nav(true, false, 'Move Devices Between Lines', 'faRetweet', 'fas', '/move-devices-between-lines', ['/home', '/admin', '/lines'], 'Move traps and devices between different trap lines with date tracking.', true, false, false, DeviceType.NONE, false),
      nav(true, false, 'Linked Devices', 'faProjectDiagram', 'fas', '/linked-devices', ['/home', '/admin', '/lines'], 'Link traps and devices across multiple lines for shared data collection.', true, false, false, DeviceType.NONE, false),
      nav(true, false, 'Enable/Disable Lines', 'faToggleOn', 'fas', '/enable-disable-lines', ['/home', '/admin', '/lines'], 'Enable or disable lines for data collection.', true, false, false, DeviceType.NONE, false),
      nav(true, false, 'Special Messages', 'faComment', 'fas', '/special-messages', ['/home', '/admin', '/lines'], 'Configure special messages displayed during data entry.', true, false, false, DeviceType.NONE, false),
      nav(true, false, 'Species/Lures/Devices', 'faCog', 'fas', '/species-lures-device-type', ['/home', '/admin'], 'Configure species, lures, and device type settings.', true, false, true, DeviceType.NONE, false),
      nav(true, false, 'Species Configuration', 'faPaw', 'fas', '/species-configuration', ['/home', '/admin', '/species-lures-device-type'], 'Add, remove, and manage species for data collection.', true, false, false, DeviceType.NONE, false),
      nav(true, false, 'Lures Configuration', 'faDrumstickBite', 'fas', '/lures-configuration', ['/home', '/admin', '/species-lures-device-type'], 'Add, remove, and manage lures and baits for trapping.', true, false, false, DeviceType.NONE, false),
      nav(true, false, 'Device Configuration', 'faCube', 'fas', '/device-configuration', ['/home', '/admin', '/species-lures-device-type'], 'Configure trap types, bait stations, and bird station devices.', true, false, false, DeviceType.NONE, false),
      nav(true, false, 'Custom Fields', 'faWrench', 'fas', '/custom-fields', ['/home', '/admin'], 'Configure custom fields, categories, and field assignments.', true, false, true, DeviceType.NONE, false),
      nav(true, false, 'Edit Categories', 'faTags', 'fas', '/edit-categories', ['/home', '/admin', '/custom-fields'], 'Edit custom field categories and values.', true, false, false, DeviceType.NONE, false),
      nav(true, false, 'CSV Files', 'faFileCsv', 'fas', '/csv-files', ['/home', '/admin'], 'Upload and download CSV files for data import/export.', true, false, true, DeviceType.NONE, false),
      nav(true, false, 'Download CSV Templates', 'faFileDownload', 'fas', '/download-csv', ['/home', '/admin', '/csv-files'], 'Download CSV templates for uploading trap locations and checks.', true, false, false, DeviceType.NONE, false),
      nav(true, false, 'Upload CSV Files', 'faFileCsv', 'fas', '/upload-csv', ['/home', '/admin', '/csv-files'], 'Upload CSV files to import trap locations and checks.', true, false, false, DeviceType.NONE, false),
      nav(true, false, 'Add Custom Field', 'faPlus', 'fas', '/add-custom-field', ['/home', '/admin', '/custom-fields'], 'Add a new custom field to the system.', true, false, false, DeviceType.NONE, false),
      nav(true, false, 'Edit Custom Fields', 'faEdit', 'fas', '/edit-custom-fields', ['/home', '/admin', '/custom-fields'], 'Edit existing custom fields and their properties.', true, false, false, DeviceType.NONE, false),
      nav(true, false, 'Line Selection', 'faRoute', 'fas', '/line-selection', ['/home', '/admin', '/custom-fields'], 'Select lines for custom field assignments.', true, false, false, DeviceType.NONE, false),
      nav(true, false, 'Category Assignment', 'faLayerGroup', 'fas', '/category-area-assignment', ['/home', '/admin', '/custom-fields'], 'Assign custom field categories to areas.', true, false, false, DeviceType.NONE, false),
      nav(true, false, 'Edit Categories', 'faTags', 'fas', '/edit-categories', ['/home', '/admin', '/custom-fields'], 'Edit custom field categories and values.', true, false, false, DeviceType.NONE, false),
      nav(true, true, 'Super Admin Actions', 'faWrench', 'fas', '/super-admin-actions', ['/home', '/admin'], 'Perform tests to check various key services are working, like emails, etc.', false, true, true, DeviceType.NONE, false),
      nav(true, false, 'Email Test', 'faEnvelope', 'fas', '/email-test', ['/home', '/admin', '/super-admin-actions'], 'Test email sending functionality.', false, true, false, DeviceType.NONE, false),
      nav(true, true, 'Platform Information', 'faInfoCircle', 'fas', '/platform-information', ['/home', '/admin', '/super-admin-actions'], 'View device and browser platform information.', false, true, false, DeviceType.NONE, false),
      nav(true, false, 'Usage Logs', 'faServer', 'fas', '/usage-logs', ['/home', '/admin', '/super-admin-actions'], 'Check on the usage logs for CatchIT.', false, true, false, DeviceType.NONE, false),
      nav(true, false, 'Fix Line Check Device Links', 'faWrench', 'fas', '/fix-line-check-device-links', ['/home', '/admin', '/super-admin-actions'], 'Fix issues with device location and line check links.', false, true, false, DeviceType.NONE, false),
      nav(true, false, 'Website Message', 'faComment', 'fas', '/website-message', ['/home', '/admin', '/super-admin-actions'], 'Configure login page message for all users.', false, true, false, DeviceType.NONE, false),
      nav(true, false, 'Change User Password', 'faKey', 'fas', '/change-user-password', ['/home', '/admin', '/super-admin-actions'], 'Reset passwords for users and generate suggestions.', false, true, false, DeviceType.NONE, false),
      nav(true, false, 'Remove Stale Registration', 'faTrashAlt', 'fas', '/remove-stale-registration', ['/home', '/admin', '/super-admin-actions'], 'Delete an unused or erroneous registration (e.g. wrong EmpID/UPI) and its login account.', false, true, false, DeviceType.NONE, false),
      nav(true, false, 'Change User Details', 'faUserEdit', 'fas', '/change-user-details', ['/home', '/admin', '/super-admin-actions'], 'Edit user personal information and contact details.', false, true, false, DeviceType.NONE, false),
    ];
  }

  /**
   * Get navigation entity by page URL
   */
  public getEntityByPage(page: string): NavEntity | undefined {
    return this.navEntities.find(entity => entity.page === page);
  }

  /**
   * Get menu items for a specific page (children in hierarchy)
   */
  public getMenuItemsForPage(page: string): NavEntity[] {
    return this.navEntities.filter(entity =>
      entity.hierarchy[entity.hierarchy.length - 1] === page
    );
  }

  /**
   * Get all navigation entities (excluding auth pages)
   */
  public getAll(): NavEntity[] {
    return this.navEntities.filter(entity =>
      !this.isAuthRelatedPage(entity.page)
    );
  }

  /**
   * Get visible navigation entities based on user permissions and device type
   */
  public async getVisibleNavigation(deviceType: DeviceType = DeviceType.NONE): Promise<NavEntity[]> {
    const isAdmin = this.catchitUserService.isAdminSync();
    const isSuperAdmin = this.catchitUserService.isSuperAdminSync();

    return this.getAll().filter(entity => {
      // Check visibility
      if (!entity.visible) return false;

      // Check device type compatibility
      if (entity.deviceType !== DeviceType.NONE && entity.deviceType !== deviceType) {
        return false;
      }

      // Check role-based visibility
      if (entity.superAdmin && !isSuperAdmin) return false;
      if (entity.admin && !isAdmin && !isSuperAdmin) return false;

      return true;
    });
  }

  /**
   * Get navigation items for home page grid
   */
  public async getHomePageNavigation(deviceType: DeviceType = DeviceType.NONE): Promise<NavEntity[]> {
    const visibleNavigation = await this.getVisibleNavigation(deviceType);
    return visibleNavigation.filter(entity =>
      entity.hierarchy.length === 1 && entity.hierarchy[0] === '/home'
    );
  }

  /**
   * Check if URL is accessible to normal users (non-admin)
   */
  public urlIsNormalUserAccessible(url: string): boolean {
    const entity = this.getEntityByPage(url);
    return entity ? !entity.admin && !entity.superAdmin : false;
  }

  /**
   * Check if URL requires admin access
   */
  public urlIsAdminAccessible(url: string): boolean {
    const entity = this.getEntityByPage(url);
    return entity ? entity.admin : false;
  }

  /**
   * Check if URL requires super admin access
   */
  public urlIsSuperAdminAccessible(url: string): boolean {
    const entity = this.getEntityByPage(url);
    return entity ? entity.superAdmin : false;
  }

  /**
   * Check if user can access a specific URL
   */
  public async canUserAccessUrl(url: string): Promise<boolean> {
    const entity = this.getEntityByPage(url);
    if (!entity) return false;

    if (entity.superAdmin) {
      return this.catchitUserService.isSuperAdminSync();
    }

    if (entity.admin) {
      return this.catchitUserService.isAdminSync();
    }

    return true; // Normal user access
  }

  /**
   * Determine device type from platform values
   */
  public getDeviceType(platformValues: string[]): DeviceType {
    if (platformValues.some(value =>
      ['tablet', 'desktop'].includes(value.toLowerCase())
    )) {
      return DeviceType.DESKTOP;
    }

    if (platformValues.some(value =>
      ['iphone', 'android', 'mobile'].includes(value.toLowerCase())
    )) {
      return DeviceType.PHONE;
    }

    return DeviceType.DESKTOP; // Default to desktop
  }

  /**
   * Get breadcrumb navigation for a page
   */
  public getBreadcrumbs(page: string): NavEntity[] {
    const entity = this.getEntityByPage(page);
    if (!entity) return [];

    const breadcrumbs: NavEntity[] = [];

    // Add hierarchy pages
    for (const hierarchyPage of entity.hierarchy) {
      const hierarchyEntity = this.getEntityByPage(hierarchyPage);
      if (hierarchyEntity) {
        breadcrumbs.push(hierarchyEntity);
      }
    }

    // Add current page
    breadcrumbs.push(entity);

    return breadcrumbs;
  }

  /**
   * Check if page is auth-related (login, signup, etc.)
   */
  private isAuthRelatedPage(page: string): boolean {
    const authPages = ['/login', '/sign-up', '/forgotten-password'];
    return authPages.includes(page);
  }

  /**
   * Add new navigation entity (for dynamic navigation)
   */
  public addNavigationEntity(entity: NavEntity): void {
    this.navEntities.push(entity);
  }

  /**
   * Remove navigation entity by page
   */
  public removeNavigationEntity(page: string): boolean {
    const index = this.navEntities.findIndex(entity => entity.page === page);
    if (index > -1) {
      this.navEntities.splice(index, 1);
      return true;
    }
    return false;
  }
}